/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useVisiblePolling } from "../lib/useVisiblePolling";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import CountdownClock from "../components/CountdownClock";
import { api } from "../lib/apiClient";
import { StatusBadge } from "../components/StatusBadge";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { fmtTimeIST } from "../lib/formatDateTime";
import GroundCommandGrid from "../components/GroundCommandGrid";

function KPI({ label, value, tone = "white", testid }) {
  const color = tone === "green" ? "#34C759" : tone === "amber" ? "#FFCC00" : tone === "red" ? "#FF3B30" : "#FFFFFF";
  return (
    <div className="mc-panel p-5" data-testid={testid}>
      <div className="mc-label">{label}</div>
      <div className="mc-metric text-5xl mt-2" style={{ color }}>{value}</div>
    </div>
  );
}

export default function CommandCentre() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!DEMO) return;
    try {
      const { data } = await api.get(`/matches/${DEMO}/dashboard`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    }
  };
  // Poll only while the tab is visible; 30 s cadence keeps the grid fresh
  // without hammering the API or churning renders every 5 seconds.
  useVisiblePolling(load, 30000, [DEMO]);

  if (!selectedMatchId) return <RequireMatch />;
  if (err) return <div className="p-8 text-incidentRed" data-testid="dashboard-error">{String(err)}</div>;
  if (!data) return <div className="p-8 text-zinc-500 font-mono-live" data-testid="dashboard-loading">▓▒░ loading command centre ░▒▓</div>;

  const { match, summary, departments, runsheet_upcoming, incidents_open } = data;
  const readyPct = summary.total_departments ? Math.round((summary.ready_departments / summary.total_departments) * 100) : 0;
  // Run Sheet timeline is only shown once we're within T-24h of the match (or already past kickoff).
  const msToMatch = match.match_date ? new Date(match.match_date).getTime() - Date.now() : Infinity;
  const showRunsheetTimeline = msToMatch <= 24 * 60 * 60 * 1000;

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-command-centre">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-cmdBorder pb-6 mb-6">
        <div>
          <div className="mc-label">Live Match</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2" data-testid="match-title">
            {match.name}
          </h1>
          <div className="text-zinc-400 mt-2 text-sm">
            <span className="font-mono-live">{match.venue}</span>
            <span className="mx-2 text-zinc-600">·</span>
            <span>{match.city}</span>
            <span className="mx-2 text-zinc-600">·</span>
            <span className="uppercase tracking-widest">{match.format}</span>
          </div>
        </div>
        <div className="hidden md:flex flex-col items-start md:items-end gap-3">
          <CountdownClock target={match.match_date} />
          <StatusBadge status={match.status === "match_day" ? "in_progress" : "pending"} kind="match" live />
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI label="Departments Ready" value={`${summary.ready_departments}/${summary.total_departments}`} tone="green" testid="kpi-ready" />
        <KPI label="Open Critical Tasks" value={summary.open_critical} tone={summary.open_critical > 0 ? "red" : "green"} testid="kpi-critical" />
        <KPI label="Venue Issues" value={summary.venue_issues} tone={summary.venue_issues > 0 ? "amber" : "green"} testid="kpi-issues" />
        <KPI label="Open Incidents" value={summary.open_incidents} tone={summary.high_severity_incidents > 0 ? "red" : summary.open_incidents ? "amber" : "green"} testid="kpi-incidents" />
      </div>

      {/* Ready gauge */}
      <div className="mc-panel p-5 mb-6" data-testid="ready-gauge">
        <div className="flex items-center justify-between mb-3">
          <div className="mc-label">Match Ready Certificate</div>
          <div className="font-mono-live text-sm text-zinc-400">{readyPct}%</div>
        </div>
        <div className="w-full h-3 bg-black border border-cmdBorder relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-readyGreen"
            style={{ width: `${readyPct}%` }}
          />
        </div>
      </div>

      {/* 3-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Departments — Redesigned Ground Command Grid */}
        <div className={showRunsheetTimeline ? "lg:col-span-8" : "lg:col-span-12"}>
          <GroundCommandGrid departments={departments} />
        </div>

        {/* Runsheet upcoming — only visible when within 24h of match start */}
        {showRunsheetTimeline && (
        <div className="lg:col-span-4 mc-panel" data-testid="runsheet-timeline-panel">
          <div className="p-4 border-b border-cmdBorder">
            <div className="mc-label">Next 6 · Run Sheet</div>
            <h3 className="font-head font-bold text-xl uppercase mt-1">Timeline</h3>
          </div>
          <div className="p-4 space-y-3 max-h-[520px] overflow-auto" data-testid="runsheet-mini">
            {runsheet_upcoming.length === 0 && <div className="text-zinc-500 text-sm">Nothing upcoming.</div>}
            {runsheet_upcoming.map((r) => (
              <Link
                to="/match-day/runsheet"
                key={r.id}
                className="block border-l-2 pl-3 pr-2 py-1 hover:bg-white/5 transition-colors duration-150"
                style={{ borderColor: r.status === "in_progress" ? "#FFCC00" : r.status === "escalated" || r.status === "delayed" ? "#FF3B30" : "#262626" }}
                data-testid={`runsheet-mini-${r.id}`}
              >
                <div className="font-mono-live text-xs text-zinc-400">
                  {fmtTimeIST(r.scheduled_time)} <span className="text-zinc-600 text-[10px]">IST</span>
                  <span className="ml-2 text-zinc-600">{r.lead_department}</span>
                </div>
                <div className="text-sm font-medium mt-0.5">{r.activity}</div>
                <div className="mt-1"><StatusBadge status={r.status} kind="runsheet" live={r.status !== "pending" && r.status !== "completed"} /></div>
              </Link>
            ))}
          </div>
        </div>
        )}

        {/* Incidents */}
        {incidents_open.length > 0 && (
          <div className="lg:col-span-12 mc-panel border-l-4" style={{ borderLeftColor: "#FF3B30" }}>
            <div className="flex items-center gap-3 p-4 border-b border-cmdBorder">
              <AlertCircle className="w-5 h-5 text-incidentRed" />
              <div>
                <div className="mc-label">Active Incidents</div>
                <h3 className="font-head font-bold text-xl uppercase">Command Alerts</h3>
              </div>
            </div>
            <div className="p-4 grid gap-3">
              {incidents_open.map((i) => (
                <Link to="/match-day/incidents" key={i.id} className="block border border-cmdBorder p-3 hover:border-incidentRed transition-colors duration-150" data-testid={`incident-alert-${i.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono-live text-xs text-zinc-400">{i.number} · {i.location}</div>
                    <StatusBadge status={i.severity === "critical" ? "escalated" : "issue_identified"} kind="incident" live />
                  </div>
                  <div className="mt-1 text-sm">{i.description}</div>
                  <div className="mt-2 text-xs text-zinc-500">Assigned → <span className="text-white">{i.assigned_dept}</span> · Cdr: {i.incident_commander}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
